import {
  type EntityId,
  type EntityKind,
  type MutationLocalId,
  type PrivacyClass,
  isLocalOnly,
  newMutationLocalId,
} from '@life-os/app-core';
import type { Clock } from './clock.js';
import { type LocalStore, Collections } from './local-store.js';

export type MutationOp = 'create' | 'update' | 'archive';

/**
 * Sync state machine:
 *   local_pending -> uploading -> synced
 *   local_pending -> uploading -> conflict_review  (both sides changed)
 *   local_pending -> uploading -> failed -> uploading (retry)
 *   strict_local  -> (terminal: never uploads)
 */
export type SyncState =
  | 'strict_local'
  | 'local_pending'
  | 'uploading'
  | 'synced'
  | 'conflict_review'
  | 'failed';

export interface Mutation {
  readonly localId: MutationLocalId;
  readonly kind: EntityKind;
  readonly entityId: EntityId;
  readonly op: MutationOp;
  readonly privacyClass: PrivacyClass;
  readonly baseVersion: number | null;
  readonly createdAt: string;
  readonly retryCount: number;
  readonly syncState: SyncState;
}

export interface MutationInput {
  readonly kind: EntityKind;
  readonly entityId: EntityId;
  readonly op: MutationOp;
  readonly privacyClass: PrivacyClass;
  readonly baseVersion: number | null;
}

export interface RemoteRecordSnapshot {
  readonly entityId: EntityId;
  readonly version: number;
}

export interface ConflictReview {
  readonly id: string;
  readonly entityId: EntityId;
  readonly localVersion: number;
  readonly remoteVersion: number;
  readonly baseVersion: number | null;
  readonly createdAt: string;
  readonly resolved: boolean;
}

export type ConflictDecision =
  | { readonly kind: 'no_conflict'; readonly winner: 'local' | 'remote' }
  | { readonly kind: 'conflict_review'; readonly localVersion: number; readonly remoteVersion: number };

/**
 * Detect a sync conflict without ever silently overwriting. A conflict exists
 * only when BOTH local and remote advanced past the shared base version.
 */
export function detectConflict(
  local: { localVersion: number; baseVersion: number | null },
  remote: RemoteRecordSnapshot,
): ConflictDecision {
  const base = local.baseVersion ?? 0;
  const localChanged = local.localVersion > base;
  const remoteChanged = remote.version > base;
  if (localChanged && remoteChanged) {
    return { kind: 'conflict_review', localVersion: local.localVersion, remoteVersion: remote.version };
  }
  if (remoteChanged) return { kind: 'no_conflict', winner: 'remote' };
  return { kind: 'no_conflict', winner: 'local' };
}

export interface MutationQueue {
  enqueue(input: MutationInput): Promise<Mutation>;
  pending(): Promise<readonly Mutation[]>;
  conflicts(): Promise<readonly ConflictReview[]>;
}

/**
 * Local-first mutation queue. Decides sync eligibility purely from privacy
 * class: strict-local / device-secret mutations are terminal and never upload.
 * It records intent for a future sync engine; it does not perform uploads here.
 */
export class LocalFirstMutationQueue implements MutationQueue {
  constructor(
    private readonly store: LocalStore,
    private readonly clock: Clock,
  ) {}

  async enqueue(input: MutationInput): Promise<Mutation> {
    const localId = newMutationLocalId();
    const mutation: Mutation = {
      localId,
      kind: input.kind,
      entityId: input.entityId,
      op: input.op,
      privacyClass: input.privacyClass,
      baseVersion: input.baseVersion,
      createdAt: this.clock.nowIso(),
      retryCount: 0,
      syncState: isLocalOnly(input.privacyClass) ? 'strict_local' : 'local_pending',
    };
    await this.store.put(Collections.mutations, String(localId), mutation);
    return mutation;
  }

  async pending(): Promise<readonly Mutation[]> {
    const all = await this.store.list<Mutation>(Collections.mutations);
    return all.filter((m) => m.syncState === 'local_pending' || m.syncState === 'failed');
  }

  async conflicts(): Promise<readonly ConflictReview[]> {
    const all = await this.store.list<ConflictReview>(Collections.conflicts);
    return all.filter((c) => !c.resolved);
  }
}
